import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, CalendarCheck, ShieldCheck, CheckCircle2, CalendarDays, ChevronDown, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { Reveal, Overline } from "@/components/Reveal";
import { api, inr, split4060, waLink } from "@/api";
import { useLang, tr } from "@/i18n";

const inputCls =
    "w-full px-4 py-3 rounded-xl bg-white border border-[#E6DBCF] text-[#241D19] text-sm focus:outline-none focus:ring-2 focus:ring-[#C85A17]/40 focus:border-[#C85A17] transition";

const TITHIS = [
    { date: "2026-09-26", day: "Sat", label: "26 Sep", tithi: "Purnima Shraddha" },
    { date: "2026-09-27", day: "Sun", label: "27 Sep", tithi: "Pratipada Shraddha" },
    { date: "2026-09-28", day: "Mon", label: "28 Sep", tithi: "Dwitiya Shraddha" },
    { date: "2026-09-29", day: "Tue", label: "29 Sep", tithi: "Tritiya Shraddha", note: "Maha Bharani" },
    { date: "2026-09-30", day: "Wed", label: "30 Sep", tithi: "Chaturthi Shraddha" },
    { date: "2026-10-01", day: "Thu", label: "1 Oct", tithi: "Panchami Shraddha" },
    { date: "2026-10-02", day: "Fri", label: "2 Oct", tithi: "Shashthi Shraddha" },
    { date: "2026-10-03", day: "Sat", label: "3 Oct", tithi: "Saptami Shraddha" },
    { date: "2026-10-04", day: "Sun", label: "4 Oct", tithi: "Ashtami Shraddha" },
    { date: "2026-10-05", day: "Mon", label: "5 Oct", tithi: "Navami Shraddha" },
    { date: "2026-10-06", day: "Tue", label: "6 Oct", tithi: "Dashami Shraddha" },
    { date: "2026-10-07", day: "Wed", label: "7 Oct", tithi: "Ekadashi Shraddha", note: "Magha Shraddha" },
    { date: "2026-10-08", day: "Thu", label: "8 Oct", tithi: "Dwadashi Shraddha" },
    { date: "2026-10-09", day: "Fri", label: "9 Oct", tithi: "Chaturdashi Shraddha" },
    { date: "2026-10-10", day: "Sat", label: "10 Oct", tithi: "Sarva Pitru Amavasya", note: "Mahalaya" },
];

export const BookingForm = ({ packages, settings, selected, onSelect }) => {
    const { lang } = useLang();
    const t = (s) => tr(lang, s);
    const [form, setForm] = useState({
        name: "", phone: "", email: "", date: "", members: "",
        notes: "", stay_type: "no",
    });
    const [pkgOpen, setPkgOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [confirmed, setConfirmed] = useState(null);
    const [paying, setPaying] = useState(false);
    const [paidId, setPaidId] = useState(null);

    useEffect(() => {
        if (!document.getElementById("rzp-js")) {
            const s = document.createElement("script");
            s.id = "rzp-js";
            s.src = "https://checkout.razorpay.com/v1/checkout.js";
            s.async = true;
            document.body.appendChild(s);
        }
    }, []);

    const pkg = useMemo(
        () => packages.find((p) => p.id === selected) || packages[0],
        [packages, selected]
    );
    const split = pkg ? split4060(pkg.price) : null;
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const whatsapp = settings?.whatsapp || "919199335409";

    const submit = async (e) => {
        e.preventDefault();
        if (!pkg) return;
        setSubmitting(true);
        try {
            const { data } = await api.post("/bookings", {
                ...form,
                members: form.members ? Number(form.members) : null,
                package_id: pkg.id,
            });
            setConfirmed(data);
            toast.success(`Booking request received — Reference #${data.id}`);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not submit booking. Please try WhatsApp.");
        } finally {
            setSubmitting(false);
        }
    };

    const payNow = async () => {
        if (!confirmed || paying) return;
        setPaying(true);
        try {
            const { data: order } = await api.post("/payments/order", { booking_id: confirmed.id });
            const rzp = new window.Razorpay({
                key: order.key_id,
                amount: order.amount,
                currency: order.currency,
                name: "Dharm Sarovar Private Limited",
                description: `${confirmed.package_name} — 40% Advance (Booking #${confirmed.id})`,
                order_id: order.order_id,
                prefill: { name: confirmed.name, contact: confirmed.phone, email: confirmed.email || "" },
                theme: { color: "#C85A17" },
                handler: async (resp) => {
                    try {
                        const { data } = await api.post("/payments/verify", { booking_id: confirmed.id, ...resp });
                        setPaidId(data.payment_id);
                        toast.success("Advance payment received — booking confirmed!");
                    } catch {
                        toast.error("Payment verification failed. Please contact us on WhatsApp.");
                    } finally {
                        setPaying(false);
                    }
                },
                modal: { ondismiss: () => setPaying(false) },
            });
            rzp.on("payment.failed", () => {
                toast.error("Payment failed or was cancelled.");
                setPaying(false);
            });
            rzp.open();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not start payment. Please try again.");
            setPaying(false);
        }
    };

    if (confirmed) {
        return (
            <section id="book" data-testid="booking-section" className="py-20 lg:py-28 bg-[#F4EDE2]">
                <div className="max-w-2xl mx-auto px-6 text-center">
                    <Reveal>
                        <div className="bg-white rounded-2xl border border-[#E5C158] p-10 shadow-lg">
                            <CalendarCheck size={44} className="mx-auto text-[#C85A17]" />
                            <h2 className="mt-4 font-display text-2xl sm:text-3xl text-[#241D19]">
                                {t("Booking Request Received")}
                            </h2>
                            <p className="mt-3 text-[#6E6259]">
                                {t("Reference")} <span className="font-semibold text-[#241D19]" data-testid="booking-reference-id">#{confirmed.id}</span> · {t(confirmed.package_name)}
                            </p>
                            <div className="mt-6 grid grid-cols-3 gap-3 text-sm">
                                <div className="bg-[#F4EDE2] rounded-xl p-4">
                                    <p className="text-xs text-[#6E6259]">{t("Total")}</p>
                                    <p className="font-semibold text-[#241D19]">{inr(confirmed.total)}</p>
                                </div>
                                <div className="bg-[#FFF8E7] rounded-xl p-4 border border-[#E5C158]">
                                    <p className="text-xs text-[#6E6259]">{t("40% Advance")}</p>
                                    <p className="font-semibold text-[#C85A17]">{inr(confirmed.advance)}</p>
                                </div>
                                <div className="bg-[#F4EDE2] rounded-xl p-4">
                                    <p className="text-xs text-[#6E6259]">{t("60% Balance")}</p>
                                    <p className="font-semibold text-[#241D19]">{inr(confirmed.balance)}</p>
                                </div>
                            </div>

                            {paidId ? (
                                <div data-testid="booking-payment-success" className="mt-6 bg-green-50 border border-green-200 rounded-xl p-5">
                                    <p className="inline-flex items-center gap-2 text-green-700 font-semibold">
                                        <CheckCircle2 size={20} /> {t("Advance Paid — Booking Confirmed")}
                                    </p>
                                    <p className="mt-2 text-xs text-green-700/80">
                                        {t("Payment ID")}: <span className="font-mono font-semibold" data-testid="booking-payment-id">{paidId}</span>
                                    </p>
                                </div>
                            ) : (
                                <>
                                    <button
                                        onClick={payNow}
                                        disabled={paying}
                                        data-testid="booking-pay-now-button"
                                        className="mt-6 w-full py-4 rounded-full bg-[#241D19] text-white font-semibold flex items-center justify-center gap-2 hover:bg-[#C85A17] transition-colors disabled:opacity-60"
                                    >
                                        <CreditCard size={18} />
                                        {paying ? t("Processing...") : `${t("Pay Advance Securely")} — ${inr(confirmed.advance)}`}
                                    </button>
                                    <p className="mt-2.5 text-[11px] text-[#B0A494]">UPI · Cards · NetBanking · Wallets — powered by Razorpay</p>
                                </>
                            )}

                            <div className="mt-6 flex items-start gap-2.5 bg-[#F4EDE2] rounded-xl p-4 text-left">
                                <ShieldCheck size={18} className="text-[#C85A17] shrink-0 mt-0.5" />
                                <p className="text-xs text-[#6E6259] leading-relaxed">
                                    {t("Payments processed securely by Razorpay. Booking is confirmed instantly after successful payment.")}
                                </p>
                            </div>
                            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
                                <button
                                    onClick={() => { setConfirmed(null); setPaidId(null); }}
                                    data-testid="booking-new-button"
                                    className="px-7 py-3.5 rounded-full border border-[#E6DBCF] text-[#241D19] font-medium hover:bg-[#F4EDE2] transition-colors"
                                >
                                    {t("New Booking")}
                                </button>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>
        );
    }

    return (
        <section id="book" data-testid="booking-section" className="py-20 lg:py-28 bg-[#F4EDE2]">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <Reveal>
                    <Overline>{t("Chapter 04 — Reserve Your Date")}</Overline>
                    <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl text-[#241D19] tracking-tight">
                        {t("Book Your Ritual")}
                    </h2>
                </Reveal>

                <Reveal delay={0.05}>
                    <div data-testid="tithi-guide" className="mt-10 bg-white rounded-2xl border border-[#E5C158] p-6 md:p-8">
                        <div className="flex items-center gap-2.5 mb-1.5">
                            <CalendarDays size={18} className="text-[#C85A17]" />
                            <h3 className="font-display text-lg sm:text-xl text-[#241D19]">{t("Pitrupaksha 2026 — Shradh Tithi Guide")}</h3>
                        </div>
                        <p className="text-xs text-[#6E6259] mb-5">{t("Select the exact tithi of your ancestor — the booking date fills automatically.")}</p>
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5">
                            {TITHIS.map((ti) => {
                                const active = form.date === ti.date;
                                return (
                                    <button
                                        type="button"
                                        key={ti.date}
                                        data-testid={`tithi-button-${ti.date}`}
                                        onClick={() => set("date", ti.date)}
                                        className={`text-left rounded-xl border px-3.5 py-3 transition-colors duration-300 ${active ? "border-[#C85A17] bg-[#FFF8E7] shadow-sm ring-1 ring-[#C85A17]/40" : "border-[#E6DBCF] hover:border-[#C85A17]/60 hover:bg-[#FAF6F0]"}`}
                                    >
                                        <p className={`text-[10px] uppercase tracking-wider ${active ? "text-[#C85A17] font-semibold" : "text-[#6E6259]"}`}>
                                            {t(ti.day)} · {ti.label}
                                        </p>
                                        <p className="font-serif-alt font-semibold text-sm text-[#241D19] mt-0.5 leading-snug">{t(ti.tithi)}</p>
                                        {ti.note && <p className="text-[10px] text-[#C85A17] mt-0.5">{t(ti.note)}</p>}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </Reveal>

                <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <Reveal className="lg:col-span-7">
                        <form onSubmit={submit} className="bg-white rounded-2xl border border-[#E6DBCF] p-6 md:p-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
                            <div className="sm:col-span-2 relative">
                                <label className="text-xs font-medium text-[#6E6259] mb-1.5 block">{t("Select Package *")}</label>
                                <button
                                    type="button"
                                    data-testid="booking-form-package-select"
                                    onClick={() => setPkgOpen((o) => !o)}
                                    className={`${inputCls} flex items-center justify-between gap-3 text-left font-medium`}
                                >
                                    <span className="truncate">{pkg ? `${t(pkg.name)} — ${inr(pkg.price)}` : t("Select Package *")}</span>
                                    <ChevronDown size={16} className={`shrink-0 text-[#C85A17] transition-transform duration-300 ${pkgOpen ? "rotate-180" : ""}`} />
                                </button>
                                <AnimatePresence>
                                    {pkgOpen && (
                                        <>
                                            <div className="fixed inset-0 z-20" onClick={() => setPkgOpen(false)} />
                                            <motion.div
                                                data-testid="package-dropdown-panel"
                                                initial={{ opacity: 0, y: -8 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, y: -8 }}
                                                transition={{ duration: 0.2, ease: "easeOut" }}
                                                className="absolute z-30 left-0 right-0 mt-2 bg-white rounded-2xl border border-[#E6DBCF] shadow-[0_16px_48px_rgba(36,29,25,0.16)] overflow-hidden"
                                            >
                                                <div className="max-h-80 overflow-y-auto divide-y divide-[#F4EDE2]">
                                                    {packages.map((p) => {
                                                        const active = p.id === pkg?.id;
                                                        const sp = split4060(p.price);
                                                        return (
                                                            <button
                                                                type="button"
                                                                key={p.id}
                                                                data-testid={`package-option-${p.id}`}
                                                                onClick={() => { onSelect(p.id); setPkgOpen(false); }}
                                                                className={`w-full text-left px-5 py-4 flex items-center justify-between gap-4 transition-colors duration-200 ${active ? "bg-[#FFF8E7]" : "hover:bg-[#FAF6F0]"}`}
                                                            >
                                                                <span>
                                                                    <span className={`block text-sm font-medium ${active ? "text-[#9C370B]" : "text-[#241D19]"}`}>{t(p.name)}</span>
                                                                    <span className="block text-xs text-[#6E6259] mt-0.5">{t(p.duration)} · {t("40% Advance")} {inr(sp.advance)}</span>
                                                                </span>
                                                                <span className="font-display font-semibold text-[#241D19] shrink-0">{inr(p.price)}</span>
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </motion.div>
                                        </>
                                    )}
                                </AnimatePresence>
                            </div>
                            <div>
                                <label className="text-xs font-medium text-[#6E6259] mb-1.5 block">{t("Customer Full Name *")}</label>
                                <input data-testid="booking-form-name-input" className={inputCls} required value={form.name} onChange={(e) => set("name", e.target.value)} placeholder={t("Your full name")} />
                            </div>
                            <div>
                                <label className="text-xs font-medium text-[#6E6259] mb-1.5 block">{t("Mobile Number *")}</label>
                                <input data-testid="booking-form-phone-input" className={inputCls} required type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+91 ..." />
                            </div>
                            <div>
                                <label className="text-xs font-medium text-[#6E6259] mb-1.5 block">{t("Email Address")}</label>
                                <input data-testid="booking-form-email-input" className={inputCls} type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@example.com" />
                            </div>
                            <div>
                                <label className="text-xs font-medium text-[#6E6259] mb-1.5 block">{t("Preferred Ritual Date *")}</label>
                                <input data-testid="booking-form-date-input" className={inputCls} required type="date" value={form.date} onChange={(e) => set("date", e.target.value)} />
                            </div>
                            <div>
                                <label className="text-xs font-medium text-[#6E6259] mb-1.5 block">{t("Number of Family Members")}</label>
                                <input data-testid="booking-form-members-input" className={inputCls} type="number" min="1" value={form.members} onChange={(e) => set("members", e.target.value)} placeholder={t("e.g. 4")} />
                            </div>
                            <div>
                                <label className="text-xs font-medium text-[#6E6259] mb-1.5 block">{t("Accommodation Assistance")}</label>
                                <select
                                    data-testid="booking-form-stay-select"
                                    className={inputCls}
                                    value={form.stay_type}
                                    onChange={(e) => set("stay_type", e.target.value)}
                                >
                                    <option value="no">{t("No stay assistance needed")}</option>
                                    <option value="ac">{t("AC Premium — ₹5,000 / day")}</option>
                                    <option value="nonac">{t("Non-AC Budget — ₹3,000 / day")}</option>
                                </select>
                            </div>
                            <div className="sm:col-span-2">
                                <label className="text-xs font-medium text-[#6E6259] mb-1.5 block">{t("Additional Requirements")}</label>
                                <textarea data-testid="booking-form-notes-input" className={`${inputCls} min-h-[90px]`} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder={t("Gotra, ancestor names, or any special requests (optional)")} />
                            </div>
                            <p className="sm:col-span-2 text-xs text-[#6E6259] leading-relaxed">
                                {t("Payments processed securely by Razorpay. Booking is confirmed instantly after successful payment.")}
                            </p>
                            <button
                                data-testid="booking-form-submit-button"
                                disabled={submitting}
                                className="sm:col-span-2 py-4 rounded-full bg-[#C85A17] text-white font-semibold hover:bg-[#9C370B] transition-colors disabled:opacity-60"
                            >
                                {submitting ? t("Submitting...") : t("Submit Booking Request")}
                            </button>
                        </form>
                    </Reveal>

                    <Reveal delay={0.15} className="lg:col-span-5">
                        <div data-testid="booking-summary-card" className="bg-[#241D19] rounded-2xl p-8 text-white sticky top-24 grain-overlay relative overflow-hidden">
                            <p className="text-xs uppercase tracking-[0.25em] text-amber-300">{t("Your Selection")}</p>
                            <h3 className="mt-3 font-serif-alt font-semibold text-2xl">{pkg && t(pkg.name)}</h3>
                            <p className="mt-1 text-sm text-stone-400">{pkg && t(pkg.duration)} · {pkg?.locations.slice(0, 2).join(" · ")}</p>

                            <div className="mt-8 space-y-4">
                                <div className="flex justify-between items-baseline border-b border-[#342A24] pb-4">
                                    <span className="text-sm text-stone-400">{t("Total Ritual Price")}</span>
                                    <span data-testid="summary-total-price" className="font-display text-2xl">{inr(pkg?.price)}</span>
                                </div>
                                <div className="flex justify-between items-baseline">
                                    <span className="text-sm text-stone-400">{t("40% Advance — pay now")}</span>
                                    <span data-testid="summary-advance-amount" className="font-display text-2xl text-amber-300">{split && inr(split.advance)}</span>
                                </div>
                                <div className="flex justify-between items-baseline border-b border-[#342A24] pb-4">
                                    <span className="text-sm text-stone-400">{t("60% Balance — 1 day before arrival")}</span>
                                    <span data-testid="summary-balance-amount" className="font-display text-2xl">{split && inr(split.balance)}</span>
                                </div>
                                {form.stay_type !== "no" && (
                                    <div className="flex justify-between items-baseline">
                                        <span className="text-sm text-stone-400">{t("Stay Assistance")}</span>
                                        <span data-testid="summary-stay-rate" className="font-display text-xl text-amber-200">
                                            + {inr(form.stay_type === "ac" ? 5000 : 3000)}{t("/ day")}
                                        </span>
                                    </div>
                                )}
                            </div>

                            <div className="mt-6 h-1.5 rounded-full overflow-hidden flex">
                                <div className="bg-[#C85A17]" style={{ width: "40%" }} />
                                <div className="bg-[#D4AF37]" style={{ width: "60%" }} />
                            </div>
                            <p className="mt-6 text-xs text-stone-500 leading-relaxed">
                                {t("Pay the 40% advance securely online after submitting — UPI, cards & netbanking via Razorpay.")}
                            </p>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
};
